/*
 * ET - Smart Home (sections/et-smart-home.liquid)
 * <et-smart-home-slider> holds the platform list. Below 750px it becomes a Swiper (one platform per slide, dots);
 * from 750px up the Swiper is destroyed and the list is a static stack. Swiper comes from window.etLoadSwiper() (et-base-head).
 * Section settings arrive as data attributes: data-slider ("false" = no slider, the list stays stacked on mobile too),
 * data-autoplay + data-autoplay-delay (ms), data-rewind (back to the first slide after the last), and translated
 * screen reader labels data-label-bullet ("[index]") and data-label-slide ("[index]", "[count]"). The optional
 * arrows ([data-et-sh-prev] / [data-et-sh-next]) and dots ([data-et-sh-dots]) are only rendered when enabled.
 * The element creates at most one Swiper while connected and destroys it when disconnected, so a theme editor
 * re-render (shopify:section:load / unload) never leaves a duplicate instance. Selected blocks slide into view and
 * pause autoplay until they are deselected.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-smart-home-slider')) return;

  const MOBILE_QUERY = '(max-width: 749px)';
  const SLIDE_SELECTOR = '[data-et-sh-slide]';

  class EtSmartHomeSlider extends HTMLElement {
    constructor() {
      super();
      this.handleMediaChange = this.handleMediaChange.bind(this);
    }

    connectedCallback() {
      this.media = window.matchMedia(MOBILE_QUERY);
      if (typeof this.media.addEventListener === 'function') {
        this.media.addEventListener('change', this.handleMediaChange);
      } else if (typeof this.media.addListener === 'function') {
        this.media.addListener(this.handleMediaChange);
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.update(), { once: true });
      } else {
        this.update();
      }
    }

    disconnectedCallback() {
      if (this.media) {
        if (typeof this.media.removeEventListener === 'function') {
          this.media.removeEventListener('change', this.handleMediaChange);
        } else if (typeof this.media.removeListener === 'function') {
          this.media.removeListener(this.handleMediaChange);
        }
      }
      this.destroy();
    }

    get sliderEnabled() {
      return this.dataset.slider !== 'false';
    }

    handleMediaChange() {
      this.update();
    }

    update() {
      if (!this.isConnected || !this.media) return;
      if (this.media.matches && this.sliderEnabled) this.init();
      else this.destroy();
    }

    get options() {
      const dotsEl = this.querySelector('[data-et-sh-dots]');
      const prevEl = this.querySelector('[data-et-sh-prev]');
      const nextEl = this.querySelector('[data-et-sh-next]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const delay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
      const a11y = { enabled: true, slideRole: 'listitem' };
      const count = this.querySelectorAll(SLIDE_SELECTOR).length;
      const bulletLabel = this.dataset.labelBullet;
      const slideLabel = this.dataset.labelSlide;
      if (bulletLabel) a11y.paginationBulletMessage = bulletLabel.split('[index]').join('{{index}}');
      if (slideLabel) {
        a11y.slideLabelMessage = slideLabel.split('[index]').join('{{index}}').split('[count]').join('{{slidesLength}}');
        a11y.firstSlideMessage = slideLabel.split('[index]').join('1').split('[count]').join(String(count));
        a11y.lastSlideMessage = slideLabel.split('[index]').join(String(count)).split('[count]').join(String(count));
      }
      if (prevEl && nextEl) {
        a11y.prevSlideMessage = prevEl.getAttribute('aria-label');
        a11y.nextSlideMessage = nextEl.getAttribute('aria-label');
      }

      return {
        slidesPerView: 1,
        spaceBetween: 24,
        speed: reducedMotion ? 0 : 400,
        watchOverflow: true,
        rewind: this.dataset.rewind === 'true',
        autoplay:
          this.dataset.autoplay === 'true' && !reducedMotion
            ? { delay, disableOnInteraction: false, pauseOnMouseEnter: true }
            : false,
        pagination: dotsEl ? { el: dotsEl, clickable: true } : false,
        navigation: prevEl && nextEl ? { prevEl, nextEl } : false,
        a11y
      };
    }

    init() {
      const swiperEl = this.querySelector('[data-et-sh-swiper]');
      if (!swiperEl || this.swiper || this.loading || typeof window.etLoadSwiper !== 'function') return;
      if (!swiperEl.querySelector(SLIDE_SELECTOR)) return;

      this.loading = true;
      Promise.resolve(window.etLoadSwiper()).catch(() => null).then((Swiper) => {
        this.loading = false;
        if (!Swiper || !this.isConnected || this.swiper || !this.media.matches || !this.sliderEnabled) return;

        const list = swiperEl.querySelector('.et-smart-home__list');
        if (list) list.scrollLeft = 0;

        this.swiper = new Swiper(swiperEl, this.options);

        if (typeof this.pendingIndex === 'number') {
          this.goTo(this.pendingIndex);
          this.pendingIndex = null;
        }
      });
    }

    destroy() {
      this.pendingIndex = null;
      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;

      const dotsEl = this.querySelector('[data-et-sh-dots]');
      if (dotsEl) dotsEl.replaceChildren();

      this.querySelectorAll(SLIDE_SELECTOR).forEach((slide) => {
        slide.removeAttribute('role');
        slide.removeAttribute('aria-label');
        slide.removeAttribute('aria-roledescription');
      });
    }

    goTo(index) {
      if (!this.swiper) return;
      if (this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
      this.swiper.slideTo(index, 0);
    }

    selectSlide(slide) {
      const index = parseInt(slide.dataset.etShIndex, 10);
      if (Number.isNaN(index)) return;

      if (this.swiper) this.goTo(index);
      else if (this.media && this.media.matches && this.sliderEnabled) this.pendingIndex = index;
    }

    resumeAutoplay() {
      if (this.swiper && this.swiper.params.autoplay && this.swiper.params.autoplay.enabled && this.swiper.autoplay) {
        this.swiper.autoplay.start();
      }
    }
  }

  window.customElements.define('et-smart-home-slider', EtSmartHomeSlider);

  const slidersIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-smart-home-slider')) : [];

  const sliderFor = (event) => {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const slider = slide && slide.closest('et-smart-home-slider');
    return slider ? { slide, slider } : null;
  };

  document.addEventListener('shopify:section:load', (event) => slidersIn(event.target).forEach((el) => el.update()));
  document.addEventListener('shopify:section:unload', (event) => slidersIn(event.target).forEach((el) => el.destroy()));

  document.addEventListener('shopify:block:select', (event) => {
    const match = sliderFor(event);
    if (match) match.slider.selectSlide(match.slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const match = sliderFor(event);
    if (match) match.slider.resumeAutoplay();
  });
})();
